import { supabase } from '../lib/supabase';

// Lead Connector API base URL
const LEAD_CONNECTOR_API_BASE = 'https://services.leadconnectorhq.com';

export interface ContactRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  tags?: string[];
  locationId?: string;
  source?: string;
  customFields?: Array<{ key: string; field_value: string }>;
}

// Interface for the actual API request format
export interface LeadConnectorContactRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth?: string;
  address1?: string;
  // address2 is not supported by the API
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  tags?: string[];
  locationId: string;
  source?: string;
  customFields?: Array<{ key: string; field_value: string }>;
}

export type UpsertResponse = {
  new: boolean;
  contact: {
    id: string;
    // ... other fields
  };
};

// Valid country codes according to ISO 3166-1 alpha-2
const VALID_COUNTRY_CODES = [
  'US', 'CA', 'GB', 'AU', 'NZ', 'IN', 'DE', 'FR', 'IT', 'ES', 'JP', 'CN',
  'BR', 'MX', 'AR', 'CL', 'CO', 'PE', 'VE', 'ZA', 'NG', 'EG', 'SA', 'AE',
  'IL', 'TR', 'RU', 'UA', 'PL', 'SE', 'NO', 'FI', 'DK', 'NL', 'BE', 'CH',
  'AT', 'PT', 'GR', 'IE', 'SG', 'MY', 'TH', 'VN', 'PH', 'ID', 'KR', 'HK'
];

// Common aliases and ISO-3 to ISO-2 conversions
const COUNTRY_ALIASES: Record<string, string> = {
  USA: 'US',
  'UNITED STATES': 'US',
  'UNITED STATES OF AMERICA': 'US',
  'U.S.': 'US',
  'U.S.A.': 'US',
  'U.S.A': 'US',
  'UNITED STATES AMERICA': 'US'
};

const ISO3_TO_ISO2: Record<string, string> = {
  USA: 'US',
  CAN: 'CA',
  MEX: 'MX',
  AUS: 'AU',
  NZL: 'NZ',
  GBR: 'GB',
  DEU: 'DE',
  FRA: 'FR',
  ITA: 'IT',
  ESP: 'ES',
  JPN: 'JP',
  CHN: 'CN',
  BRA: 'BR',
  ARG: 'AR',
  CHL: 'CL',
  COL: 'CO',
  PER: 'PE',
  VEN: 'VE',
  ZAF: 'ZA',
  NGA: 'NG',
  EGY: 'EG',
  SAU: 'SA',
  ARE: 'AE',
  ISR: 'IL',
  TUR: 'TR',
  RUS: 'RU',
  UKR: 'UA',
  POL: 'PL',
  SWE: 'SE',
  NOR: 'NO',
  FIN: 'FI',
  DNK: 'DK',
  NLD: 'NL',
  BEL: 'BE',
  CHE: 'CH',
  AUT: 'AT',
  PRT: 'PT',
  GRC: 'GR',
  IRL: 'IE',
  SGP: 'SG',
  MYS: 'MY',
  THA: 'TH',
  VNM: 'VN',
  PHL: 'PH',
  IDN: 'ID',
  KOR: 'KR',
  HKG: 'HK',
  IND: 'IN'
};

export function normalizeCountryCode(country?: string): string | undefined {
  if (!country) {
    return undefined;
  }

  const trimmed = country.trim();
  if (!trimmed) {
    return undefined;
  }

  const upper = trimmed.toUpperCase();

  if (COUNTRY_ALIASES[upper]) {
    return COUNTRY_ALIASES[upper];
  }

  if (upper.length === 2 && VALID_COUNTRY_CODES.includes(upper)) {
    return upper;
  }

  if (upper.length === 3 && ISO3_TO_ISO2[upper]) {
    return ISO3_TO_ISO2[upper];
  }

  return VALID_COUNTRY_CODES.includes(upper) ? upper : undefined;
}

/**
 * Sanitize contact data for Lead Connector API
 * - Converts dob to dateOfBirth
 * - Ensures country is a valid code
 * - Formats phone numbers
 * - Ensures customFields are in the correct format
 * - Handles address2 by concatenating it with address1
 */
function sanitizeContactData(data: ContactRequest): LeadConnectorContactRequest {
  const sanitized = { ...data } as any;
  
  // Remove dob and use dateOfBirth instead
  if ('dob' in sanitized) {
    sanitized.dateOfBirth = sanitized.dob;
    delete sanitized.dob;
  }
  
  // Ensure country is a valid code
  if (sanitized.country) {
    const normalizedCountry = normalizeCountryCode(sanitized.country);
    if (normalizedCountry) {
      sanitized.country = normalizedCountry;
    } else {
      console.warn(`Invalid country code: ${sanitized.country}, defaulting to US`);
      sanitized.country = 'US';
    }
  } else {
    sanitized.country = 'US';
  }
  
  // Format phone number if present (ensure it has country code)
  if (sanitized.phone && !sanitized.phone.startsWith('+')) {
    // Assume US number if no country code
    sanitized.phone = sanitized.phone.replace(/^\D*1?\D*/, '+1');
  }

  // Handle address2 by concatenating it with address1
  if (sanitized.address2) {
    sanitized.address1 = sanitized.address1 ? `${sanitized.address1}, ${sanitized.address2}` : sanitized.address2;
    delete sanitized.address2;
  }

  // Ensure customFields are in the correct format
  if (sanitized.customFields && Array.isArray(sanitized.customFields)) {
    // Convert customFields to the format expected by the API
    sanitized.customFields = sanitized.customFields.map((field: {key?: string; field_value?: string}) => {
      // Ensure each field has key and field_value properties
      if (field && typeof field === 'object') {
        return {
          key: field.key || '',
          field_value: field.field_value || ''
        };
      }
      return field;
    });
  }
  
  return sanitized as LeadConnectorContactRequest;
}

export class LeadConnectorApi {
  private apiKey: string;

  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  /**
   * Upsert a contact directly to the Lead Connector API
   * This bypasses the proxy and makes a direct request with CORS mode set to no-cors
   */
  async upsertContact(contactData: ContactRequest): Promise<{ contactId: string; IsNew: boolean }> {
    try {
      console.log('Making direct API request to Lead Connector');
      
      // Sanitize data for Lead Connector API
      const sanitizedData = sanitizeContactData(contactData);
      
      // Ensure source is set
      const upsertData = {
        ...sanitizedData,
        source: sanitizedData.source || '4D EMR Integration'
      };
      
      // Log the request payload for debugging
      console.log('Upsert request payload:', JSON.stringify(upsertData, null, 2));
      
      // Make direct request to Lead Connector API
      const response = await fetch(`${LEAD_CONNECTOR_API_BASE}/contacts/upsert`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Version': '2021-07-28',
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(upsertData),
        mode: 'no-cors' // This will make the request without CORS restrictions
      });
      
      // With no-cors mode, we won't be able to read the response body
      // So we'll just assume success if we get here without an error
      return {
        contactId: 'unknown', // We can't access the actual ID with no-cors
        IsNew: true // We don't know if it's new or not
      };
    } catch (error) {
      console.error('Error making direct upsert to Lead Connector:', error);
      throw error;
    }
  }

  /**
   * Alternative upsert method using XMLHttpRequest for better error handling
   */
  upsertContactXHR(contactData: ContactRequest): Promise<{ contactId: string; IsNew: boolean }> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Sanitize data for Lead Connector API
      const sanitizedData = sanitizeContactData(contactData);
      
      // Ensure source is set
      const upsertData = {
        ...sanitizedData,
        source: sanitizedData.source || '4D EMR Integration'
      };
      
      // Log the request payload for debugging
      console.log('Upsert XHR request payload:', JSON.stringify(upsertData, null, 2));
      
      xhr.open('POST', `${LEAD_CONNECTOR_API_BASE}/contacts/upsert`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${this.apiKey}`);
      xhr.setRequestHeader('Version', '2021-07-28');
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve({
              contactId: response.contact?.id || 'unknown',
              IsNew: response.IsNew || false
            });
          } catch (e) {
            // If we can't parse the response, assume success
            resolve({
              contactId: 'unknown',
              IsNew: true
            });
          }
        } else {
          console.error('XHR Error:', xhr.status, xhr.statusText);
          try {
            // Try to parse the error response
            const errorResponse = JSON.parse(xhr.responseText);
            console.error('API Error Response:', errorResponse);
            reject(new Error(`Lead Connector API request failed: ${xhr.status} ${xhr.statusText} - ${JSON.stringify(errorResponse)}`));
          } catch (e) {
            reject(new Error(`Lead Connector API request failed: ${xhr.status} ${xhr.statusText}`));
          }
        }
      };
      
      xhr.onerror = function() {
        console.error('XHR Network Error');
        reject(new Error('Network error occurred'));
      };
      
      xhr.send(JSON.stringify(upsertData));
    });
  }
}

// Create a singleton instance
let leadConnectorApiInstance: LeadConnectorApi | null = null;

export function initializeLeadConnectorApi(apiKey: string): LeadConnectorApi {
  leadConnectorApiInstance = new LeadConnectorApi(apiKey);
  return leadConnectorApiInstance;
}

export function getLeadConnectorApiInstance(): LeadConnectorApi | null {
  return leadConnectorApiInstance;
}
